import { useEffect } from "react";
import { Link } from "react-router-dom";

export default function PrivacyPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-[#050505] min-h-screen text-[#94A3B8]">
      <div className="max-w-3xl mx-auto px-6 py-20">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#38BDF8] hover:text-white transition-colors mb-10"
        >
          ← Back to Home
        </Link>

        <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
        <p className="text-sm text-[#64748B] mb-10">Last updated: May 29, 2026</p>

        <div className="space-y-8 text-sm leading-relaxed">
          <section>
            <h2 className="text-white font-semibold text-base mb-2">1. Introduction</h2>
            <p>
              Tyo Solution ("we", "our", "us") is committed to protecting your personal information.
              This Privacy Policy explains what data we collect, how we use it, and your rights
              regarding that data when you use tyosolution.com or any of our sub-domain services.
            </p>
          </section>

          <section>
            <h2 className="text-white font-semibold text-base mb-2">2. Information We Collect</h2>
            <ul className="list-disc list-inside space-y-1 text-[#64748B]">
              <li><span className="text-[#94A3B8]">Account data:</span> name, email address, and password hash when you register.</li>
              <li><span className="text-[#94A3B8]">Google OAuth:</span> name and email from Google when you sign in with Google.</li>
              <li><span className="text-[#94A3B8]">Payment data:</span> handled entirely by Paddle; we do not store card details.</li>
              <li><span className="text-[#94A3B8]">Usage data:</span> API request logs, credit consumption, and feature usage for service improvement.</li>
              <li><span className="text-[#94A3B8]">Analytics:</span> page visits and interactions via Google Analytics (anonymised).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white font-semibold text-base mb-2">3. File Uploads</h2>
            <p>
              Files you upload for processing (invoices, CVs, PDFs, images) are processed entirely
              in memory. <strong className="text-white">We do not store, log, or share your uploaded files.</strong>{" "}
              Files are discarded immediately after processing is complete.
            </p>
          </section>

          <section>
            <h2 className="text-white font-semibold text-base mb-2">4. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-1 text-[#64748B]">
              <li>To provide, maintain, and improve our services.</li>
              <li>To manage your account and process payments.</li>
              <li>To send transactional emails (account verification, payment receipts).</li>
              <li>To monitor for abuse and ensure security.</li>
              <li>To analyse aggregate usage trends for product development.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white font-semibold text-base mb-2">5. Data Sharing</h2>
            <p>
              We do not sell your personal data. We share data only with the following trusted
              third-party providers:
            </p>
            <ul className="list-disc list-inside space-y-1 mt-2 text-[#64748B]">
              <li><span className="text-[#94A3B8]">Paddle</span> — payment processing.</li>
              <li><span className="text-[#94A3B8]">Google Analytics</span> — website analytics (anonymised).</li>
              <li><span className="text-[#94A3B8]">MongoDB Atlas</span> — encrypted cloud database for account data.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white font-semibold text-base mb-2">6. Cookies</h2>
            <p>
              We use cookies and similar technologies to maintain your session and support Google
              Analytics. You may disable cookies in your browser settings, though some features may
              not function correctly as a result.
            </p>
          </section>

          <section>
            <h2 className="text-white font-semibold text-base mb-2">7. Data Retention</h2>
            <p>
              Account data is retained as long as your account is active. You may request deletion
              of your account and associated data at any time by contacting us. Usage logs are
              retained for up to 90 days for security monitoring.
            </p>
          </section>

          <section>
            <h2 className="text-white font-semibold text-base mb-2">8. Your Rights</h2>
            <p className="mb-2">Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc list-inside space-y-1 text-[#64748B]">
              <li>Access the personal data we hold about you.</li>
              <li>Correct inaccurate data.</li>
              <li>Request deletion of your data.</li>
              <li>Object to or restrict certain processing.</li>
              <li>Data portability (receive your data in a structured format).</li>
            </ul>
            <p className="mt-2">
              To exercise any of these rights, email us at{" "}
              <a href="mailto:hi@tyosolution.com" className="text-[#38BDF8] hover:underline">
                hi@tyosolution.com
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-white font-semibold text-base mb-2">9. Security</h2>
            <p>
              We implement industry-standard security measures including TLS encryption for data in
              transit and bcrypt hashing for passwords. However, no system is completely secure and
              we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-white font-semibold text-base mb-2">10. Children's Privacy</h2>
            <p>
              Our services are not directed to individuals under 13 years of age. We do not
              knowingly collect personal information from children.
            </p>
          </section>

          <section>
            <h2 className="text-white font-semibold text-base mb-2">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy periodically. The updated date will be reflected at
              the top of this page. Continued use of our services constitutes acceptance of the
              updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-white font-semibold text-base mb-2">12. Contact</h2>
            <p>
              Questions about this policy? Contact us at{" "}
              <a href="mailto:hi@tyosolution.com" className="text-[#38BDF8] hover:underline">
                hi@tyosolution.com
              </a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
